import React, { memo, useCallback } from "react";
import colors from "app/colors";
import {
  Divider,
  VStack,
  Text,
  HStack,
  Button,
  Spacer,
} from "@chakra-ui/react";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { HappycoLogoIcon, HelpIcon } from "modules/icons";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import HappycoMiniLogoIcon from "modules/icons/happyco-mini-logo-icon";
import HappycoWorkOrderPriority from "./happyco-work-order-priority";
import HappycoWorkOrderStatus from "./happyco-work-order-status";
import HappycoWorkOrderAssignee from "./happyco-work-order-assignee";
import HappycoWorkOrderScheduledTime from "./happyco-work-order-scheduled-time";
import useHappycoWorkOrder from "./use-happyco-work-order";
import HappycoWorkOrderLocation from "./happyco-work-order-location";
import HappycoWorkOrderType from "./happyco-work-order-type";
import ComposeMessageThreadSubject from "modules/messaging/_shared/compose-message-thread-subject";

interface IProps {
  draftId: string;
}

const HappycoComposeHeader = ({ draftId }: IProps) => {
  const { draft } = useMessageThreadDraft({ draftId });
  const { openModal } = useOverlay();

  const { happycoWorkOrder } = useHappycoWorkOrder({
    happycoWorkOrderId: draft?.happycoWorkOrderId,
  });

  const opelHelpModal = useCallback(() => {
    openModal({
      type: MODAL_TYPE.HELP,
      helpModal: {
        icon: <HappycoMiniLogoIcon boxSize={12} fill={colors.blackSecondary} />,
        title: "HappyCo Threads",
        message:
          "Threads created from HappyCo work orders will be lorem ipsum dolor sit amet consectur.",
      },
    });
  }, [openModal]);

  if (!happycoWorkOrder) {
    return null;
  }

  return (
    <VStack spacing={4} pt={4} w="full" alignItems="flex-start">
      <Divider />
      <VStack spacing={1} w="full" alignItems="flex-start">
        <HappycoLogoIcon fill={colors.happycoBlue} w={97} h={17} />
        <HStack spacing={0}>
          <Text fontSize={14} fontWeight={500} color={colors.grayMain}>
            A new thread will be created from the work order info in HappyCo
          </Text>
          <Button onClick={opelHelpModal} variant="hover" size="xs">
            <HelpIcon fill={colors.gray40} boxSize={4} />
          </Button>
        </HStack>
      </VStack>
      <ComposeMessageThreadSubject draftId={draftId} />
      <VStack
        w="full"
        bg={colors.whiteTertiary}
        borderRadius={8}
        py={2}
        px={4}
        spacing={3}
      >
        <HStack w="full" spacing={1}>
          <HappycoWorkOrderLocation happycoWorkOrder={happycoWorkOrder} />
          <Spacer />
          <HappycoWorkOrderType happycoWorkOrder={happycoWorkOrder} />
        </HStack>
        <HStack w="full" spacing={1}>
          <HappycoWorkOrderPriority happycoWorkOrder={happycoWorkOrder} />
          <Spacer />
          <HappycoWorkOrderStatus happycoWorkOrder={happycoWorkOrder} />
        </HStack>
        <HStack w="full">
          <HappycoWorkOrderAssignee happycoWorkOrder={happycoWorkOrder} />
          <Spacer />
          <HappycoWorkOrderScheduledTime happycoWorkOrder={happycoWorkOrder} />
        </HStack>
      </VStack>
    </VStack>
  );
};

export default memo(HappycoComposeHeader);
